import React, {FC, useEffect, useRef, useMemo} from 'react';
import Linkify from 'linkify-react';
import {IntermediateRepresentation} from 'linkifyjs';
import './chargingStationModal.css';
import TypographyHeading from '../../components/Typography/TypographyHeading';
import TypographyBody from '../../components/Typography/TypographyBody';
import Icon from '../../components/Icon';
import useMediaQuery from '../../hooks/useMediaQuery';
import {Poi} from './evMap';

import capitalize from 'lodash/capitalize';
import {pluralize} from '../../utils';
import isEmpty from 'lodash/isEmpty';
import orderBy from 'lodash/orderBy';
import {CTA} from '@volkswagen-onehub/components-core';

interface ChargingStationModalProps {
  onClose: () => void;
  chargingStationDetails: Poi;
  maxHeight: number;
  onCTAClick?: () => void;
}

const ChargingStationModal: FC<ChargingStationModalProps> = ({
  onClose,
  chargingStationDetails,
  onCTAClick,
  maxHeight,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const [isExpanded, setIsExpanded] = React.useState(false);
  const {isMobile} = useMediaQuery();
  const modalMaxHeight = maxHeight > 0 ? maxHeight : 440;
  const {
    name,
    location: {
      address: {zipcode, city, street, state, number},
      position,
    },
    charging: {charging_devices, is_paid_parking} = {
      charging_devices: [],
      total_count_charging_points: 0,
    },
    is_open_24h,
    information,
  } = chargingStationDetails;
  const parkingFee =
    typeof is_paid_parking === 'boolean'
      ? is_paid_parking
        ? 'Parking: Pay'
        : 'Parking: Free'
      : undefined;

  const connectorTypeCount = useMemo(() => {
    const connectors = charging_devices
      .flatMap((chargingDevice) => chargingDevice.charging_points)
      .flatMap((chargingPoint) => chargingPoint.connectors)
      .filter(Boolean);

    const typeCount = connectors.reduce<Record<string, number>>((acc, cur) => {
      if (!(cur in acc)) {
        acc[cur] = 1;
      } else {
        acc[cur] += 1;
      }
      return acc;
    }, {});

    return typeCount;
  }, [charging_devices]);

  const informationTexts = information?.map((item) => item.text) || [];
  const shouldShowExpandButton =
    typeof is_open_24h === 'boolean' ||
    parkingFee ||
    informationTexts.length > 0;

  const renderLink = ({attributes, content}: IntermediateRepresentation) => {
    const {href, ...props} = attributes;
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        style={{color: '#6A767D', lineBreak: 'anywhere'}}
        {...props}
      >
        {content}
      </a>
    );
  };

  const onClickExpandDetail = () => {
    setIsExpanded((prev) => !prev);
  };

  const renderDetails = () => {
    if (!isExpanded) {
      return null;
    }

    return (
      <>
        {is_open_24h && (
          <div className="charging-station-modal__open-time">
            <Icon icon="clock" />
            <TypographyBody size="small" color="#6A767D">
              24/7
            </TypographyBody>
          </div>
        )}
        {parkingFee && (
          <div className="charging-station-modal__parking-fee">
            <Icon icon="parking-garage" />
            <TypographyBody size="small" color="#6A767D">
              {parkingFee}
            </TypographyBody>
          </div>
        )}
        {informationTexts.length > 0 && (
          <div className="charging-station-modal__information">
            <Icon icon="info" />
            <div className="charging-station-modal__information-detail">
              {informationTexts.map((detail, index) => (
                <TypographyBody
                  size="small"
                  color="#6A767D"
                  key={index}
                  style={{wordBreak: 'break-word'}}
                >
                  <Linkify options={{render: renderLink}}>{detail}</Linkify>
                </TypographyBody>
              ))}
            </div>
          </div>
        )}
      </>
    );
  };

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, []);

  return (
    <div
      className="modal-overlay charging-station-modal"
      style={{bottom: isMobile ? '32px' : '40px'}}
    >
      <div
        className="modal"
        style={{
          maxWidth: isMobile ? '325px' : '400px',
          maxHeight: isExpanded ? `${modalMaxHeight}px` : 'fit-content',
          overflow: 'auto',
        }}
        ref={modalRef}
      >
        <div className="modal-content">
          <div className="charging-station-modal__header">
            <div>
              <TypographyHeading tag="h6" color="black" size="small" bold>
                {name}
              </TypographyHeading>
              <TypographyBody
                size="small"
                color="#6A767D"
                style={{marginTop: '4px'}}
              >
                {number || ''} {street || ''}, {city || ''} {state || ''}{' '}
                {zipcode || ''}
              </TypographyBody>
            </div>
            {shouldShowExpandButton && (
              <button
                className="charging-station-modal__header-detail-button"
                onClick={onClickExpandDetail}
              >
                {isExpanded ? (
                  <Icon icon="close" />
                ) : (
                  <Icon icon="info" size={22} />
                )}
              </button>
            )}
          </div>
          <div className="charging-station-modal__divider" />
          <div className="charging-station-modal__detail">
            {!isEmpty(connectorTypeCount) && (
              <div className="charging-station-modal__charging-points">
                <div className="charging-station-modal__charging-points-title">
                  <Icon icon="charging-type" />
                  <TypographyBody size="small" color="#000">
                    EVUp
                  </TypographyBody>
                </div>
                {orderBy(Object.keys(connectorTypeCount)).map((key) => {
                  const label = capitalize(key);
                  const count = connectorTypeCount[key];

                  return (
                    <div
                      key={key}
                      className="charging-station-modal__charging-point-item"
                    >
                      <TypographyBody
                        size="small"
                        color="#6A767D"
                        style={{marginLeft: 24}}
                      >
                        {label}
                      </TypographyBody>
                      <div className="charging-station-modal__charging-point-count">
                        <TypographyBody size="small" color="#029640">
                          {pluralize(count, 'Plug')}
                        </TypographyBody>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {renderDetails()}
            <a
              href={`https://www.google.com/maps?q=${position.lat},${position.lon}`}
              aria-label="Get directions"
              target="_blank"
              rel="noreferrer"
              onClick={onCTAClick}
            >
              <CTA
                tag="button"
                ariaLabel="Get directions"
                size="large"
                stretchContent
              >
                Get directions
              </CTA>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChargingStationModal;
